import React, { useContext, useEffect, useRef, useState } from 'react';
//@ts-ignore
import * as styles from './filtered-content.module.css';
import ShowMoreArrow from '../Global/ShowMoreArrow';
import { ContentPreview, FilteredContentProps } from './index';
import TagSelector from './TagSelector';
import { getMultiChoice } from '../Global/DataUtils';
import BlogExploration from './BlogExploration';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import NewsMediaExploration from './NewsMediaExploration';
import FaqExploration from './FaqExploration';
import ResearchExploration from './ResearchExploration';
import Faq, { isRenderableFaq } from '../Faq';
import { Faq as FaqType } from '../Global/DataUtils/faqQueries';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';

export type TagData = {
    taxonomy: string;
    codename: string;
    count: number;
};
export type SortedTag = {
    [name: string]: TagData[];
};

export type SortedTags = SortedTag[];

interface Tag {
    codename: string;
    name: string;
}

export interface TagCategory {
    [category: string]: Tag[];
}

interface Props extends FilteredContentProps {
    tagCategories: TagCategory[];
    content: ContentPreview[];
    filter?: { codename: string; name: string };
}

const FilteredContent: React.FC<Props> = ({
    headline,
    tagCategories,
    content,
    desktop_value_to_show,
    mobile_value_to_show,
    view_more_value_desktop,
    view_more_value_mobile,
    filter,
    featured_tiles,
    kontentItemId,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const faqRef = useRef(null);
    const pageMetadata = useContext(PageMetadataContext);
    const safeContent = Array.isArray(content) ? content.filter(Boolean) : [];
    const safeTagCategories = Array.isArray(tagCategories) ? tagCategories : [];

    const isResearchType = props.content_type?.value[0]?.name === 'Research';
    const desktopValueToShow =
        desktop_value_to_show?.value && desktop_value_to_show.value >= 0
            ? desktop_value_to_show.value
            : 0;

    const mobileValueToShow =
        mobile_value_to_show?.value && mobile_value_to_show.value >= 0
            ? mobile_value_to_show.value
            : 0;

    const mobileShowMoreValue =
        view_more_value_mobile?.value && view_more_value_mobile?.value >= 0
            ? mobile_value_to_show?.value
            : 10;

    const desktopShowMoreValue =
        view_more_value_desktop?.value && view_more_value_desktop?.value >= 0
            ? view_more_value_desktop?.value
            : 10;
    const featuredTiles = featured_tiles?.value;
    const faqFeaturedTiles = props.faq_featured_tiles?.value;
    const sortedTags = getSortedTags(
        safeTagCategories,
        safeContent,
        isResearchType
    );
    const tagNames = Object.keys(sortedTags).map((tagName) => {
        let taxonomies = sortedTags[tagName];
        let totalContent = 0;

        taxonomies.map((tagData: TagData) => {
            totalContent = totalContent + tagData.count;
        });

        return { name: tagName, count: totalContent };
    });

    const [tagsClicked, setTagsClicked] = useState<string[]>(
        filter?.name ? [filter.name] : []
    );

    const layout = getMultiChoice(props.layout);

    const truncatedContentToShow = breakpoints.md
        ? desktopValueToShow
        : mobileValueToShow;

    const newsMediaShowMoreValue = breakpoints.md
        ? desktopShowMoreValue
        : mobileShowMoreValue;

    const [contentItemsToShow, setContentItemsToShow] = useState<number>(
        truncatedContentToShow
    );
    const [featuredfaq, setSearchedfaq] = useState<any>();

    useEffect(() => {
        setContentItemsToShow(truncatedContentToShow);
    }, [truncatedContentToShow]);

    const [filteredContent, setFilteredContent] =
        useState<ContentPreview[]>(safeContent);

    const featuredItem = props.featured_item?.value[0];
    const defineVisibleContent = featuredItem
        ? [featuredItem, ...filteredContent?.slice(0, contentItemsToShow - 1)]
        : filteredContent?.slice(0, contentItemsToShow);

    const [visibleContent, setVisibleContent] =
        useState<ContentPreview[]>(defineVisibleContent);

    let tiles: ContentPreview[] =
        faqFeaturedTiles != undefined ? faqFeaturedTiles : [];
    tiles = [...tiles, ...filteredContent];

    const [contentFilter, setContentFilter] = useState<ContentPreview[]>(tiles);

    let allfaqs = [];
    if (props.content_type?.value[0]?.name == 'FAQ') {
        allfaqs = contentFilter;
    }

    const allFaqItems: FaqType[] = (() => {
        if (featuredItem) {
            const rest = Array.isArray(filteredContent) ? filteredContent : [];
            return [featuredItem, ...rest].filter(isRenderableFaq);
        }
        return (Array.isArray(contentFilter) ? contentFilter : []).filter(
            isRenderableFaq
        );
    })();

    const centerItemPlacement = Math.floor(
        (Math.min(truncatedContentToShow, safeContent.length) - 1) / 2
    );

    useEffect(() => {
        const searchParam =
            typeof window !== 'undefined' ? window.location.search : '';
        const urlParams = new URLSearchParams(searchParam);
        const id = urlParams.get('faqID');
        const searchedfaq = safeContent.find((faq) => faq.id === id);
        if (searchedfaq) {
            setSearchedfaq(searchedfaq);
        }
    }, []);

    useEffect(() => {
        if (featuredfaq && faqRef.current) {
            faqRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [featuredfaq]);

    useEffect(() => {
        setVisibleContent(defineVisibleContent);
    }, [contentItemsToShow, filteredContent, contentFilter]);

    const handleTagsClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const tagClicked = event.currentTarget.getAttribute('data-tag') || '';

        let newTagsClicked: string[] = [];
        if (tagsClicked.includes(tagClicked)) {
            newTagsClicked = tagsClicked.filter((tag) => tag !== tagClicked);
        } else if (tagClicked === '') {
            newTagsClicked = [];
        } else {
            newTagsClicked = [...tagsClicked, tagClicked];
        }
        setTagsClicked(newTagsClicked);

        if (newTagsClicked.length === 0) {
            setFilteredContent(safeContent);
            let filter: ContentPreview[] =
                faqFeaturedTiles != undefined ? faqFeaturedTiles : [];
            filter = [...filter, ...safeContent];
            setContentFilter(filter);
            return;
        }

        const newFilteredContent = filterContent(
            safeContent,
            newTagsClicked,
            sortedTags
        );
        setFilteredContent(newFilteredContent);
        setContentFilter(newFilteredContent);
    };

    const showContent = () => {
        if (isResearchType) {
            return (
                <ResearchExploration
                    content={visibleContent}
                    cta_label={props.cta_label}
                    centerItemPlacement={centerItemPlacement}
                    center_item={props.center_item?.value[0]}
                    featured_tiles={featuredTiles}
                    tags={tagNames}
                    handleTagsClick={handleTagsClick}
                    tagsClicked={tagsClicked}
                    view_more_label={props.view_more_label}
                    headline={headline}
                    featured_tiles_headline={props.featured_tiles_headline}
                    reveal_descriptions={props.reveal_descriptions}
                />
            );
        }
        switch (layout) {
            case 'news_and_media':
                return (
                    <NewsMediaExploration
                        content={visibleContent}
                        cta_label={props.cta_label}
                        centerItemPlacement={centerItemPlacement}
                        center_item={props.center_item?.value[0]}
                        featured_tiles={featuredTiles}
                    />
                );
                break;
            case 'faqs':
                return (
                    <FaqExploration
                        content={allFaqItems}
                        visibleCount={contentItemsToShow}
                        maxShowInGrid={truncatedContentToShow}
                        useGrid={props.use_grid_view}
                    />
                );
                break;
            case 'blog':
                return (
                    <BlogExploration
                        content={visibleContent}
                        preferredLanguage={pageMetadata.preferredLanguage}
                        show_blog_subscription_form={
                            props.show_blog_subscription_form
                        }
                        blog_topic={props.blog_topic}
                        cta_label={props.cta_label}
                        submit_cta_label={props.submit_cta_label}
                        form_headline={props.form_headline}
                        email_address_field={props.email_address_field}
                        full_name_field={props.full_name_field}
                        interested_in_field={props.interested_in_field}
                    />
                );
            default:
                return null;
        }
    };

    const handleShowMorePostsClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newContentItemsToShow = newsMediaShowMoreValue
            ? contentItemsToShow + newsMediaShowMoreValue
            : contentItemsToShow + truncatedContentToShow;
        setContentItemsToShow(newContentItemsToShow);
        setVisibleContent(defineVisibleContent);
    };

    const showMorePostsButton = () => {
        if (filteredContent.length > visibleContent.length) {
            return (
                <ShowMoreArrow
                    btnText={props.view_more_label?.value || 'Show More'}
                    strokeColor="#505251"
                    handleClick={handleShowMorePostsClick}
                    transform={'0'}
                />
            );
        } else if (allFaqItems.length > contentItemsToShow) {
            return (
                <ShowMoreArrow
                    btnText={props.view_more_label?.value || 'Show More'}
                    strokeColor="#505251"
                    handleClick={handleShowMorePostsClick}
                    transform={'0'}
                />
            );
        }
    };

    useEffect(() => {
        if (tagsClicked.length) {
            const newFilteredContent = filterContent(
                safeContent,
                tagsClicked,
                sortedTags
            );
            setFilteredContent(newFilteredContent);
            setContentFilter(newFilteredContent);
        }
    }, []);

    return (
        <section
            className={styles.wrapper}
            ref={faqRef}
            data-kontent-item-id={kontentItemId}
        >
            {!isResearchType && (
                <>
                    <h2
                        className={styles.headline}
                        data-kontent-element-codename="headline"
                    >
                        {headline?.value || ''}
                    </h2>
                    {props.content_type?.value[0]?.name == 'FAQ' && (
                        <div
                            style={
                                featuredfaq
                                    ? {
                                          margin: '2rem 4rem',
                                          background: 'var(--color-gray)',
                                      }
                                    : {}
                            }
                        >
                            {isRenderableFaq(featuredfaq) ? (
                                <>
                                    <>
                                        <Faq
                                            faq={featuredfaq}
                                            expanded={true}
                                        />
                                    </>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                    )}
                    <TagSelector
                        tags={tagNames}
                        handleTagsClick={handleTagsClick}
                        tagsClicked={tagsClicked}
                        view_more_label={props.view_more_label}
                    />
                </>
            )}
            {showContent()}
            {showMorePostsButton()}
        </section>
    );
};

export default FilteredContent;

function getSortedTags(
    tagCategories: TagCategory[],
    content: ContentPreview[],
    isResearchType: boolean
) {
    let sortedTags = {} as SortedTags;

    tagCategories.map((tagCategory) => {
        const category = Object.keys(tagCategory)[0];
        const tags = tagCategory[category];

        for (let i = 0; i < tags?.length; i++) {
            const name = tags[i].name;
            const tagData = {
                codename: tags[i].codename,
                taxonomy: category,
                count: 0,
            };
            tagData.count = getTagCount(tagData, content);
            if (tagData.count === 0) continue;
            if (sortedTags[name]) {
                sortedTags[name] = [tagData, ...sortedTags[name]];

                return;
            }

            sortedTags[name] = [tagData];
        }
    });

    if (isResearchType) {
        return sortedTags;
    }
    return Object.keys(sortedTags)
        .sort()
        .reduce((result, key) => {
            result[key] = sortedTags[key];
            return result;
        }, {});
}

function getTagCount(
    tag: { codename: string; taxonomy: string },
    contentItems: ContentPreview[]
) {
    let count = 0;

    contentItems.map((content: ContentPreview) => {
        const tagTaxonomy = tag.taxonomy;
        const contentTaxonomy = content?.elements?.[tagTaxonomy]?.value;

        if (!Array.isArray(contentTaxonomy)) return;

        contentTaxonomy.map((taxonomy) => {
            if (taxonomy?.codename === tag.codename) {
                ++count;
            }
        });
    });

    return count;
}

function filterContent(content, tagsClicked, sortedTags) {
    return content.filter((contentItem) => {
        let itemMatch = false;

        for (let i = 0; i < tagsClicked.length; i++) {
            if (itemMatch) {
                break;
            }

            const tagName = tagsClicked[i];
            const tagTaxonomies = sortedTags[tagName];

            if (!Array.isArray(tagTaxonomies)) {
                continue;
            }

            tagTaxonomies.map(
                (tagTaxonomy: { taxonomy: string; codename: string }) => {
                    if (itemMatch) {
                        return;
                    }

                    const taxonomyCategory: any = tagTaxonomy.taxonomy;
                    const taxonomyCodename: string = tagTaxonomy.codename;
                    const contentItemTaxonomyValue =
                        contentItem?.elements?.[taxonomyCategory]?.value;

                    if (!Array.isArray(contentItemTaxonomyValue)) {
                        return;
                    }

                    contentItemTaxonomyValue.map((itemTax) => {
                        if (itemTax?.codename === taxonomyCodename) {
                            itemMatch = true;
                        }
                    });
                }
            );
        }

        return itemMatch;
    });
}
